import { useMutation, useQuery } from "@tanstack/react-query"
import { Form, InputNumber, Switch } from "antd"
import { CustomInput, CustomSelect } from "components/new-stuff/form"
import FormSectionWrapper from "components/new-stuff/form/FormSectionWrapper"
import PrevNextButton from "components/new-stuff/form/PrevNextButton"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { useNavigate } from "react-router-dom"
import { ewaAxiosInstance } from "utils/request"
import {
  CreatePolicyPayload,
  ICreatePolicyResponse,
} from "../interface/policies"
import { useAppNotificationStore } from "stores"
import { IEmployeesData } from "../interface/employees"
import FilterPolicy from "./components/FilterPolicy"
import { useState } from "react"

const CreatePolicy = () => {
  const { toast } = useAppNotificationStore()

  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [openFilter, setOpenFilter] = useState(false)
  const employees = Form.useWatch("employees", form)

  const {
    isLoading: isemployeesLoading,
    data: employeesData,
    refetch,
  } = useQuery<IEmployeesData>({
    queryKey: [`ewa-employees`],
    queryFn: async () => {
      const res = await ewaAxiosInstance.get(`/v1/employees?limit=0`)
      return res.data
    },

    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })

  const { mutate: createPolicy, isLoading } = useMutation({
    mutationFn: async (data: CreatePolicyPayload) => {
      const res = await ewaAxiosInstance.post(`/v1/group-policies`, data)
      return res.data
    },
    onSuccess(data: ICreatePolicyResponse) {
      navigate(-1)
      form.resetFields()
      data?.message && toast.success(data?.message)
    },
    onError(error: any) {
      toast.error(error?.response?.data?.message)
    },
  })

  const onFinish = (values: any) => {
    const { name, limit, approval } = values

    const body = {
      name,
      approvalRequired: approval,
      withdrawalPercent: limit,
      employeeIds: employees || [],
    }
    createPolicy(body)
  }
  const handleApplyFilter = (selectedEmployees: string[]) => {
    const filteredEmpl = employeesData?.data?.docs
      ?.filter((emp) => selectedEmployees.includes(emp?.padeUserId))
      ?.map((emp) => emp._id)

    form.setFieldValue("employees", filteredEmpl)
    setOpenFilter(false)
  }

  return (
    <DashboardWrapper>
      <FormSectionWrapper
        title="Create EWA Policy"
        subtitle="Set different access levels for specific employee or groups based on their role, performance, or other factors"
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
        isActive={true}
        footerBtns={
          <PrevNextButton
            nextBtnTitle="Create Policy"
            prevBtnTitle="Cancel"
            submitting={isLoading}
            onClickPrev={() => {
              navigate("/dashboard/ewa/policy-management")
            }}
          />
        }
        initialValues={{
          approval: false,
        }}
      >
        <div className="flex flex-col px-4 md:px-6 xl:px-[60px] pb-4">
          <CustomInput
            label={"Policy Name "}
            name="name"
            required
          />

          <Form.Item
            label={
              <span className="font-circular text-padeBlack text-sm">
                Withdrawal Limit (%)
              </span>
            }
            name="limit"
            className=" "
            rules={[
              {
                required: true,
                message: "Please enter this field",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (value) {
                    const amount = parseFloat(value)
                    if (amount > 100) {
                      return Promise.reject("Amount cannot be more than 100")
                    } else {
                      return Promise.resolve()
                    }
                  } else {
                    return Promise.resolve()
                  }
                },
              }),
            ]}
          >
            <InputNumber<number>
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/,/g, "") as unknown as number}
              className="font-circular text-neutral400 w-full"
              placeholder="e.g., 50%, 70%, or 100%"
              suffix="%"
            />
          </Form.Item>

          <div className=" relative">
            <div className="flex gap-4 justify-end absolute z-10  top-1 right-0">
              <button
                type="button"
                className="cursor-pointer text-blue-800"
                onClick={() => {
                  setOpenFilter(true)
                }}
              >
                Filter
              </button>
              <button
                type="button"
                className="cursor-pointer text-blue-800"
                onClick={() => {
                  const allpersons = employeesData?.data?.docs?.map(
                    (person) => person._id
                  )
                  // )
                  form.setFieldValue("employees", allpersons)
                }}
              >
                Add All
              </button>
              <button
                type="button"
                className="cursor-pointer text-blue-800"
                onClick={() => {
                  form.setFieldValue("employees", undefined)
                }}
              >
                Remove All
              </button>
            </div>

            <CustomSelect
              showSearch
              mode="multiple"
              label={
                <span className="font-circular text-padeBlack text-sm">
                  Select Employees{" "}
                </span>
              }
              name="employees"
              required
              options={
                employeesData &&
                employeesData?.data?.docs?.map((option) => ({
                  label: `${option?.firstName} ${option?.lastName} `,
                  value: option._id,
                }))
              }
              isLoading={isemployeesLoading}
              placeholder="Please Select"
              onChange={(selected) => {}}
            />
          </div>
          <div className=" flex items-center gap-2 ">
            <p className="text-padeBlack text-sm font-circular ">
              Approval Requirement{" "}
            </p>
            <Form.Item
              name="approval"
              rootClassName="!mb-0"
            >
              <Switch className="bg-[#ededed]" />
            </Form.Item>
          </div>

          {openFilter && (
            <FilterPolicy
              open={openFilter}
              setOpenFilter={setOpenFilter}
              handleApplyFilter={handleApplyFilter}
            />
          )}
        </div>
      </FormSectionWrapper>
    </DashboardWrapper>
  )
}

export default CreatePolicy
export interface IEmployees {
  success: boolean
  code: number
  message: string
  data: {
    docs: IEmployee[]
  }
}

export interface IEmployee {
  id: number
  key: string
  text: string
  id_number: string
}
